import React, { useState, useEffect } from 'react';
import { metricsEngine } from '../../services/MetricsEngine';
import {
    TrendingUp,
    Users,
    CreditCard,
    Target,
    Activity,
    DollarSign,
    Briefcase,
    LayoutGrid,
    ArrowUpRight,
    ArrowDownRight,
    Wallet
} from 'lucide-react';

const CompassWidget = () => {
    const [timeframe, setTimeframe] = useState('monthly');
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadMetrics = async () => {
        setLoading(true);
        try {
            const data = await metricsEngine.calculateMetrics(timeframe);
            setMetrics(data);
        } catch (error) {
            console.error("Compass Load Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMetrics();
    }, [timeframe]); // Reload when timeframe changes

    if (!metrics) {return <div className="p-8 text-center text-slate-400">Initializing Compass...</div>;}
    if (loading && !metrics) {return <div className="p-8 text-center text-slate-400">Calibrating...</div>;}

    const { financials, crm, ops, networkStats } = metrics;

    const formatCurrency = (val) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(val || 0);
    const formatNumber = (val) => new Intl.NumberFormat('en-ZA').format(val || 0);

    return (
        <div className="bg-white border border-slate-100 rounded-[2.5rem] p-8 shadow-lg hover:shadow-xl transition-shadow duration-500">
            {/* Header / Controls */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-600 text-white p-3 rounded-2xl shadow-lg shadow-indigo-200">
                        <LayoutGrid size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">The Compass</h2>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">360° Business Performance</p>
                    </div>
                </div>

                {/* Timeframe Toggles */}
                <div className="flex bg-slate-100 p-1 rounded-xl">
                    {['daily', 'weekly', 'monthly', 'quarterly', 'ytd'].map(tf => (
                        <button
                            key={tf}
                            onClick={() => setTimeframe(tf)}
                            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${timeframe === tf ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>
            </div>

            {/* MAIN GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 text-slate-800">

                {/* COLUMN 1: REVENUE & CASH (The Engine) */}
                <div className="space-y-6">
                    <SectionTitle icon={<DollarSign size={16} />} title="Cash Flow Engine" color="text-emerald-600" />

                    <MetricRow label="Revenues / Inflows" value={formatCurrency(financials.revenues)} isPrimary />
                    <MetricRow label="Payments Received" value={formatCurrency(financials.paymentsReceived)} />
                    <MetricRow label="Debtors (Outstanding)" value={formatCurrency(financials.debtors)} color="text-amber-600" />

                    <div className="h-px bg-slate-100 my-4" />

                    <MetricRow label="Total Outflows" value={formatCurrency(financials.totalOutflows)} color="text-rose-600" />
                    <MetricRow label="Net Profit" value={formatCurrency(financials.netProfit)} isPrimary color={financials.netProfit >= 0 ? "text-emerald-600" : "text-rose-600"} />
                    <MetricRow label="Gross Margin" value={`${financials.grossMargin.toFixed(1)}%`} />
                    <div className="flex gap-4 mt-2">
                        <div className="flex-1 bg-rose-50 p-2 rounded-lg text-center">
                            <span className="block text-[9px] font-bold text-rose-400 uppercase">Burn Rate</span>
                            <span className="text-sm font-black text-rose-700">{formatCurrency(financials.burnRate)}</span>
                        </div>
                        <div className="flex-1 bg-emerald-50 p-2 rounded-lg text-center">
                            <span className="block text-[9px] font-bold text-emerald-400 uppercase">Runway</span>
                            <span className="text-sm font-black text-emerald-700">{Math.min(99, financials.runway).toFixed(1)} Mo</span>
                        </div>
                    </div>

                    <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-slate-500 uppercase">Cash Balance</span>
                            <Wallet size={16} className="text-slate-400" />
                        </div>
                        <div className="text-2xl font-black text-slate-900">{formatCurrency(financials.cashBalance)}</div>
                    </div>
                </div>

                {/* COLUMN 2: SALES & PIPELINE (The Future) */}
                <div className="space-y-6 lg:border-l lg:border-slate-100 lg:pl-8">
                    <SectionTitle icon={<Target size={16} />} title="Sales Pipeline" color="text-blue-600" />

                    <MetricRow label="Prospects Hotlist" value={crm.hotlistCount} />
                    <MetricRow label="MQLs Generated" value={crm.mqls} />
                    <MetricRow label="Prospecting Offers" value={crm.prospectingOffers} />

                    <div className="grid grid-cols-2 gap-4 my-2">
                        <MiniCard label="Discovery Calls" value={crm.discoveryCalls} />
                        <MiniCard label="Demos / Pres." value={crm.demos} />
                    </div>

                    <MetricRow label="SQLs (Sales Qualified)" value={crm.sqls} isPrimary />
                    <MetricRow label="Pipeline Value (SQLs)" value={formatCurrency(crm.sqlValue)} color="text-blue-600" />
                    <MetricRow label="Quotes Issued" value={crm.quotesCount} />
                </div>

                {/* COLUMN 3: OPS & ALLOCATIONS (The Foundation) */}
                <div className="space-y-6 lg:border-l lg:border-slate-100 lg:pl-8">
                    <SectionTitle icon={<Briefcase size={16} />} title="Ops & Allocations" color="text-purple-600" />

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <MiniCard label="New Customers" value={ops.newCustomers} color="text-purple-600" />
                        <MiniCard label="New Domains" value={ops.newDomains} />
                    </div>

                    <div className="h-px bg-slate-100 my-4" />
                    <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Network Health (Total)</h4>

                    <MetricRow label="BizBuilding Coaches" value={networkStats.coaches} color="text-indigo-600" />
                    <MetricRow label="Coaching Clients" value={networkStats.coachingClients} />
                    <MetricRow label="Self-Paced Clients" value={networkStats.selfPacedClients} />
                    <MetricRow label="Referral Partners" value={networkStats.partners} />

                    <div className="h-px bg-slate-100 my-4" />
                    <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3">Reserves & Allocations</h4>

                    <MetricRow label="Salaries Reserved" value={formatCurrency(financials.salariesReserved)} />
                    <MetricRow label="Capital Reserves" value={formatCurrency(financials.capitalReserves)} />
                    <MetricRow label="Incentives Reserved" value={formatCurrency(financials.incentivesReserved)} />
                    <MetricRow label="Investments" value={formatCurrency(financials.investmentsReserved)} />

                    <div className="mt-4 flex items-center gap-2 text-xs font-medium text-slate-400 bg-slate-50 p-3 rounded-xl">
                        <Activity size={14} />
                        <span>Interest Income: {formatCurrency(financials.interestIncome)}</span>
                    </div>
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    Live Data from Local Sync • Last Updated: {new Date().toLocaleTimeString()}
                </span>
            </div>
        </div>
    );
};

// --- Sub-components ---

const SectionTitle = ({ icon, title, color }) => (
    <div className={`flex items-center gap-2 mb-4 ${color}`}>
        {icon}
        <h3 className="text-sm font-black uppercase tracking-widest">{title}</h3>
    </div>
);

const MetricRow = ({ label, value, isPrimary, color = "text-slate-800" }) => (
    <div className="flex justify-between items-center group">
        <span className="text-sm font-bold text-slate-500 group-hover:text-slate-700 transition-colors">{label}</span>
        <span className={`${isPrimary ? 'text-xl' : 'text-base'} font-black ${color} tracking-tight`}>
            {value}
        </span>
    </div>
);

const MiniCard = ({ label, value, color = "text-slate-800" }) => (
    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-center">
        <div className={`text-xl font-black ${color}`}>{value}</div>
        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mt-1">{label}</div>
    </div>
);

export default CompassWidget;
